/**
 * THE NUMBERS — the WHAT'S NEW card's third band (DESIGN-CHANGELOG
 * 2026-08-24), in the detail page's cell vocabulary: a mono `.11em` label, a
 * 20px value, a mono sub-line. Four cells on desktop in one row, folded 2×2 on
 * the phone.
 *
 * One component for both trees because the band is the same band — only the
 * fold differs, and that is a prop. The values come from `whatsNewVM.nums`,
 * which is where the honesty lives: no `LOW EVER` without a price history, and
 * no `SEEDED SNAPSHOT` label over numbers that are not seeded (docs/porting/Y.md
 * C1/C2). This file only lays them out.
 *
 * The divider is a real 1px element rather than a border on the cell, because a
 * 2×2 fold wants dividers only between columns — a border would draw one under
 * the top row as well.
 */

import type { whatsNewVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

type Nums = NonNullable<Extract<ReturnType<typeof whatsNewVM>, { nums: unknown }>['nums']>;

const LABEL = {
  fontFamily: MONO,
  fontSize: '8.5px',
  letterSpacing: '.11em',
  color: 'var(--gt-muted)',
} as const;

const SUB = {
  fontFamily: MONO,
  fontSize: '8.5px',
  letterSpacing: '.05em',
  color: 'var(--gt-muted-2)',
} as const;

function Cell({
  label,
  big,
  bigClr,
  sub,
  badge,
}: {
  label: string;
  big: string;
  bigClr?: string | undefined;
  sub: string;
  /** The discount chip, on PRICE only. `undefined` renders nothing at all. */
  badge?: string | undefined;
}): JSX.Element {
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={LABEL}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        <span style={{ fontFamily: MONO, fontSize: 20, lineHeight: 1, color: bigClr ?? 'var(--gt-text)' }}>{big}</span>
        {badge ? (
          <span
            style={{
              fontFamily: MONO,
              fontWeight: 700,
              fontSize: '9.5px',
              background: 'var(--acc)',
              color: 'var(--gt-ink)',
              padding: '3px 5px',
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <span style={{ ...SUB, overflowWrap: 'anywhere' }}>{sub}</span>
    </div>
  );
}

const Rule = (): JSX.Element => <div style={{ width: 1, flex: 'none', background: 'var(--gt-line)' }} />;

export function NumbersBand({ nums, fold }: { nums: Nums; fold?: boolean }): JSX.Element {
  const cells = [
    <Cell key="rev" label="REVIEWS" big={nums.revBig} bigClr={nums.revClr} sub={nums.revSub} />,
    <Cell
      key="price"
      label="PRICE"
      big={nums.priceBig}
      sub={nums.priceSub}
      badge={nums.hasDisc ? nums.disc : undefined}
    />,
    <Cell key="seats" label="SEATS" big={nums.seatBig} sub={nums.seatSub} />,
    <Cell key="peak" label="PEAK" big={nums.peakBig} sub={nums.peakSub} />,
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        padding: fold ? '13px 14px' : '14px 18px',
        background: 'var(--gt-panel)',
        borderTop: '1px solid var(--gt-line)',
        borderBottom: '1px solid var(--gt-line)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ ...LABEL, fontSize: 9 }}>THE NUMBERS</span>
      </div>
      {fold ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 14 }}>
            {cells[0]}
            <Rule />
            {cells[1]}
          </div>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 14 }}>
            {cells[2]}
            <Rule />
            {cells[3]}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 16 }}>
          {cells[0]}
          <Rule />
          {cells[1]}
          <Rule />
          {cells[2]}
          <Rule />
          {cells[3]}
        </div>
      )}
    </div>
  );
}
